import { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from '@/modules/Dropdown';
import { Input } from '@/modules/Input';
import { InnerButton } from '@/modules/InnerButton';
import { DatePicker } from '@/modules/DatePicker';
import { Tag } from '@/modules/Tag';
import {
  AdvancedFilterConfig,
  FilterOperator,
  FILTER_OPERATORS,
  FilterColumnOption,
} from '../../types/filter';
import { cn } from '@/shared/utils/cn';
import { FilterIcon } from '../atoms/FilterIcon';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

// Mapa de operadores a símbolos/texto para la descripción
const OPERATOR_SYMBOLS: Record<FilterOperator, string> = {
  equals: '=',
  notEquals: '≠',
  contains: 'contiene',
  notContains: 'no contiene',
  startsWith: 'empieza con',
  endsWith: 'termina con',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  between: 'entre',
  isEmpty: 'está vacío',
  isNotEmpty: 'no está vacío',
  in: 'es uno de',
  afterDate: '>',
  beforeDate: '<',
  thisMonth: 'es este mes',
  lastMonth: 'es mes pasado',
  thisYear: 'es este año',
  lastYear: 'es año pasado',
};
interface FilterCardProps {
  filter: AdvancedFilterConfig;
  columnData: FilterColumnOption;
  onUpdate: (filterId: string, updates: Partial<AdvancedFilterConfig>) => void;
  onRemove: (filterId: string) => void;
  className?: string;
}

export const FilterCard = ({
  filter,
  columnData,
  onUpdate,
  onRemove,
  className,
}: FilterCardProps) => {
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator | ''>(
    filter.operator || '',
  );
  const [value, setValue] = useState(filter.value || '');
  const [value2, setValue2] = useState(filter.value2 || '');

  // Estado para rastrear si hay foco en cualquier elemento del card
  const [hasAnyFocus, setHasAnyFocus] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Detectar foco usando focus-within
  useEffect(() => {
    const cardElement = cardRef.current;
    if (!cardElement) return;

    const handleFocusIn = () => setHasAnyFocus(true);
    const handleFocusOut = (e: FocusEvent) => {
      // Verificar si el nuevo foco está fuera del card
      if (!cardElement.contains(e.relatedTarget as Node)) {
        setHasAnyFocus(false);
      }
    };

    cardElement.addEventListener('focusin', handleFocusIn);
    cardElement.addEventListener('focusout', handleFocusOut);

    return () => {
      cardElement.removeEventListener('focusin', handleFocusIn);
      cardElement.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Para checkboxes múltiples (operador "in")
  // Inicializar con los objetos completos de las opciones basándose en los valores del filtro
  const [selectedOptions, setSelectedOptions] = useState<
    Array<{ value: string | number; label: string }>
  >(() => {
    if (Array.isArray(filter.value) && columnData.options && filter.value !== null) {
      const filterValues = filter.value as (string | number)[];
      return columnData.options.filter((opt) => filterValues.includes(opt.value));
    }
    return [];
  });

  // Filtrar operadores según el tipo de datos de la columna
  const availableOperators = FILTER_OPERATORS.filter((op) =>
    op.dataTypes.includes(columnData.dataType),
  ).map((op) => ({
    value: op.value,
    label: op.label,
  }));

  const operatorConfig = FILTER_OPERATORS.find((op) => op.value === selectedOperator);

  const isListWithMultipleOptions =
    columnData.dataType === 'list' && !!columnData.options && selectedOperator === 'in';

  const isListWithSingleOption =
    columnData.dataType === 'list' &&
    !!columnData.options &&
    (selectedOperator === 'equals' || selectedOperator === 'notEquals');

  // Manejar cambio de valores del dropdown (cuando cambian las selecciones)
  const handleDropdownValueChange = (newValue: string | number | (string | number)[]) => {
    // newValue es un array de valores [id1, id2, ...], necesitamos convertirlo a objetos completos
    if (Array.isArray(newValue) && columnData.options) {
      const newSelectedOptions = columnData.options.filter((opt) => newValue.includes(opt.value));
      setSelectedOptions(newSelectedOptions);
    } else {
      setSelectedOptions([]);
    }
  };

  // Actualizar el filtro cuando cambien los valores
  useEffect(() => {
    if (selectedOperator) {
      // Para operador "in", extraer solo los valores del array de objetos
      if (selectedOperator === 'in') {
        const values = selectedOptions.map((opt) => opt.value);
        onUpdate(filter.id, {
          operator: selectedOperator as FilterOperator,
          value: values,
          value2: undefined,
        });
      } else {
        onUpdate(filter.id, {
          operator: selectedOperator as FilterOperator,
          value: value,
          value2: value2 || undefined,
        });
      }
    }
  }, [selectedOperator, value, value2, selectedOptions, filter.id, onUpdate]);

  // Limpiar selectedOptions cuando cambia el operador y no es "in"
  useEffect(() => {
    if (selectedOperator !== 'in') {
      setSelectedOptions([]);
    }
  }, [selectedOperator]);

  // Generar la descripción del filtro para mostrar debajo de la tarjeta
  const filterDescription = useMemo(() => {
    // No mostrar si no hay operador seleccionado
    if (!selectedOperator) return null;

    const symbol = OPERATOR_SYMBOLS[selectedOperator] || selectedOperator;
    const columnName = `"${columnData.label}"`;

    // Operadores sin valor (isEmpty, isNotEmpty, etc.)
    if (!operatorConfig?.requiresValue) {
      return `${columnName} ${symbol}`;
    }

    // Operador "in" (es uno de) - solo mostrar si hay opciones seleccionadas
    if (selectedOperator === 'in') {
      if (selectedOptions.length > 0) {
        const valuesText = selectedOptions.map((opt) => `"${opt.label}"`).join(', ');
        return `${columnName} ${symbol} [${valuesText}]`;
      }
      // No mostrar si no hay opciones seleccionadas
      return null;
    }

    // Operador "between" - solo mostrar si ambos valores están presentes
    if (selectedOperator === 'between') {
      if (value && value2) {
        return `${columnName} ${symbol} "${value}" y "${value2}"`;
      }
      // No mostrar si faltan valores
      return null;
    }

    // Operadores con un valor - solo mostrar si hay valor
    if (value) {
      return `${columnName} ${symbol} "${value}"`;
    }

    // No mostrar si no hay valor
    return null;
  }, [selectedOperator, columnData.label, value, value2, selectedOptions, operatorConfig]);

  return (
    <div
      ref={cardRef}
      className={cn(
        'ui-flex ui-flex-col ui-gap-16 ui-rounded-surface-card ui-border ui-p-16 ui-transition-all ui-duration-200 ui-ease-in-out',
        {
          'ui-border-focus ui-bg-surface-brand': hasAnyFocus,
          'ui-border-default ui-bg-fill': !hasAnyFocus,
        },
        className,
      )}
    >
      {/* Header */}
      <div className="ui-flex ui-items-center ui-justify-between">
        <div className="ui-flex ui-items-center ui-gap-8">
          {/* <Icon name="TaskListOutlined" color="blue" /> */}

          <FilterIcon type={columnData.dataType} />

          <p className="ui-text-body-md-regular ui-font-body-md-bold ui-text-primary">
            {columnData.label}
          </p>
        </div>
        <InnerButton
          theme="solid"
          color="secondary"
          size="xxs"
          tooltip={{ label: 'Quitar', position: 'top-left' }}
          iconProps={{ icon: faXmark }}
          onClick={() => onRemove(filter.id)}
          pill="pill"
        />
      </div>

      {/* Selector de operador */}
      <Dropdown
        field={{ placeholder: 'Selecciona un operador', size: 'xs' }}
        items={{
          options: availableOperators,
          valueName: 'label',
          idName: 'value',
          defaultOptionSelected: false,
        }}
        values={{ value: selectedOperator }}
        events={{ onValueChange: (val) => setSelectedOperator(val as FilterOperator) }}
        popover={{ absolute: true }}
      />

      {/* Inputs de valor (solo visible si el operador requiere valor) */}
      {selectedOperator && operatorConfig?.requiresValue && (
        <div className="ui-flex ui-flex-col ui-gap-2">
          {/* Si es operador "in" (Es uno de) y tipo list, mostrar dropdown con checkboxes */}
          {isListWithMultipleOptions ? (
            <Dropdown
              field={{ label: 'Filtrar Por', placeholder: 'Selecciona opciones...', size: 'xs' }}
              items={{
                options: columnData.options,
                valueName: 'label',
                idName: 'value',
                defaultOptionSelected: false,
              }}
              values={{ value: selectedOptions.map((opt) => opt.value), hasCurrent: false }}
              events={{ onValueChange: handleDropdownValueChange }}
              multi={{ multiple: true, selectionSummary: 'tags' }}
              checkbox={{ withCheckbox: true }}
              menuSearch={{ searchable: true }}
              popover={{ absolute: true }}
            />
          ) : isListWithSingleOption ? (
            <Dropdown
              field={{ label: 'Valor', placeholder: 'Selecciona una opción...', size: 'xs' }}
              items={{
                options: columnData.options,
                valueName: 'label',
                idName: 'value',
                defaultOptionSelected: false,
              }}
              values={{
                value:
                  typeof value === 'string' || typeof value === 'number' ? value : '',
              }}
              events={{
                onValueChange: (newValue) => {
                  if (Array.isArray(newValue)) {
                    setValue(newValue[0] ?? '');
                    return;
                  }

                  setValue(newValue);
                },
              }}
              menuSearch={{ searchable: true }}
              popover={{ absolute: true }}
            />
          ) : (
            <>
              {/* Para date, mostrar DatePicker */}
              {columnData.dataType === 'date' ? (
                <div className="ui-flex ui-flex-col ui-gap-8">
                  <div className="ui-flex ui-flex-col ui-gap-4">
                    <DatePicker
                      selectedDate={
                        value && typeof value === 'string'
                          ? (() => {
                            // Parsear el string YYYY-MM-DD en hora local
                            const [year, month, day] = value.split('-').map(Number);
                            return new Date(year, month - 1, day);
                          })()
                          : null
                      }
                      onDateChange={(date) => {
                        if (date) {
                          // Formatear la fecha en hora local (evitar conversión UTC)
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          setValue(`${year}-${month}-${day}`);
                        } else {
                          setValue('');
                        }
                      }}
                      closeOnSelect
                    />
                  </div>
                  {operatorConfig.requiresSecondValue && (
                    <div className="ui-flex ui-flex-col ui-gap-4">
                      <DatePicker
                        selectedDate={
                          value2 && typeof value2 === 'string'
                            ? (() => {
                              // Parsear el string YYYY-MM-DD en hora local
                              const [year, month, day] = value2.split('-').map(Number);
                              return new Date(year, month - 1, day);
                            })()
                            : null
                        }
                        onDateChange={(date) => {
                          if (date) {
                            // Formatear la fecha en hora local (evitar conversión UTC)
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            setValue2(`${year}-${month}-${day}`);
                          } else {
                            setValue2('');
                          }
                        }}
                        closeOnSelect
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Para text, number y list (equals, notEquals), mostrar input normal */}
                  <Input
                    placeholder="Ingresa el valor"
                    value={
                      typeof value === 'string' || typeof value === 'number' ? String(value) : ''
                    }
                    onValueChange={setValue}
                    size="xs"
                    fullWidth
                    type={columnData.dataType === 'number' ? 'number' : 'text'}
                  />
                  {operatorConfig.requiresSecondValue && (
                    <Input
                      placeholder="Ingresa el segundo valor"
                      value={
                        typeof value2 === 'string' || typeof value2 === 'number'
                          ? String(value2)
                          : ''
                      }
                      onValueChange={setValue2}
                      size="xs"
                      fullWidth
                      type={columnData.dataType === 'number' ? 'number' : 'text'}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {filterDescription && <Tag label={filterDescription} color="accent" />}
    </div>
  );
};
